/**
 * Sidebar - Simplified internal team discussion sidebar
 */

import React, { memo, useState, useRef, useEffect } from 'react';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';

import SidebarMessageList from './SidebarMessageList';
import ConversationBetween from '../MessageDetails/ConversationBetween';
import { useSidebarMessages, useUpdateReadPointer } from '../../hooks';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { useUserDetailsContext } from 'context/userDetailsContext';

function Sidebar({ messageDetails, hasPermission }) {
  const [messageText, setMessageText] = useState('');
  const [justSent, setJustSent] = useState(false);
  const justSentTimerRef = useRef(null);
  const { addToast } = useToast();
  const { userDetails } = useUserDetailsContext();

  const { messages, totalCount, isLoading, isSending, hasMore, isLoadingMore, errorMessage, scrollContainerRef, sendMessage, loadMore } =
    useSidebarMessages(messageDetails?.case?.case_id, messageDetails?.message_thread_id);

  // Cleanup send timer on unmount
  useEffect(() => () => clearTimeout(justSentTimerRef.current), []);

  // ── Auto-mark as read when viewing thread ─────────────────────────────────
  const updateReadPointerMutation = useUpdateReadPointer();
  useEffect(() => {
    if (hasPermission && messageDetails?.message_thread_id && messageDetails?.has_new_sidebar_messages === true) {
      updateReadPointerMutation.mutate({
        message_thread_id: messageDetails.message_thread_id,
        user_id: userDetails?.firm_user_id,
        message_type: 'sidebar',
        unread: false,
      });
    }
  }, [messageDetails?.message_thread_id, messageDetails?.is_read, hasPermission]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!messageText.trim() || isSending) return;

    await sendMessage(messageText)
      .then(() => {
        setMessageText('');
        // Trigger send button fly-out animation
        clearTimeout(justSentTimerRef.current);
        setJustSent(true);
        justSentTimerRef.current = setTimeout(() => setJustSent(false), 500);
      })
      .catch((error) => {
        console.error(error);
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, 'Failed to send message');
      });
  };

  return (
    <div className="d-flex flex-column sidebar">
      {/* Participants header */}
      <ConversationBetween messageDetails={messageDetails} fromSidebar />

      {/* Messages list */}
      <div className="flex-grow-1 overflow-hidden">
        <SidebarMessageList
          scrollContainerRef={scrollContainerRef}
          messages={messages}
          totalCount={totalCount}
          isLoading={isLoading}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          loadMore={loadMore}
        />
      </div>

      {/* Input form */}
      <form onSubmit={handleSend} className="mt-2">
        <div style={{ position: 'relative' }}>
          <InputTextarea
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            rows={3}
            autoResize
            autoFocus
            className="w-100"
            placeholder={errorMessage || 'Send a Message...'}
            disabled={!hasPermission || !!errorMessage || isSending}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend(e);
              }
            }}
          />
          <Button
            type="submit"
            disabled={!hasPermission || !!errorMessage || isSending || !messageText.trim()}
            className={`p-button-secondary sent-btn${justSent ? ' send-btn-success' : ''}`}
            loading={isSending}
            icon="pi pi-send"
            style={{ position: 'absolute', right: 6, bottom: 12 }}
            title={hasPermission ? 'Send message' : 'You are not part of this thread'}
          />
        </div>
      </form>
    </div>
  );
}

export default memo(Sidebar);
